import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DateTime } from 'luxon';
import { API, FeUtil } from '../../../hezky-routes';

@Injectable({
  providedIn: 'root'
})
export class AssetsService {

  constructor(private feUtil: FeUtil) { }

  public addAsset(payload: any): Observable<any> {
    return this.feUtil.httpSvc.post(API.ASSET.CREATE_ASSET, payload)
  }

  public updateAsset(id: string | number, payload: any): Observable<any> {
    return this.feUtil.httpSvc.patch(API.ASSET.UPDATE_ASSET, { asset_id: Number(id), updateField: { ...payload } })
  }

  public decommissionAsset(payload: any): Observable<any> {
    return this.feUtil.httpSvc.post(API.ASSET.DECOMMISSION_ASSET, payload)
  }

  public swapSensor(payload: any): Observable<any> {
    return this.feUtil.httpSvc.post(API.ASSET.SWAP_SENSOR, payload)
  }

  public getAssets(payload: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.ASSET.LIST, payload)
  }

  public getFilters(payload: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.ASSET.FILTERS, payload)
  }

  public getById(id:string | number): Observable<any> {
    return this.feUtil.httpSvc.get(API.ASSET.BY_ID(id))
  }

  public getAlarmByAssetId(id: string | number, loc_id: string | number): Observable<any> {
    return this.feUtil.httpSvc.get(API.ASSET.ALARM.GET_ALARMS_BY_ASSET, { asset_id: id, location_id: loc_id })
  }

  public createAlarm(payload: any): Observable<any> {
    return this.feUtil.httpSvc.post(API.ASSET.ALARM.CREATE_ALARM, payload)
  }

  public updateAlarm(id: string | number, payload: any, remarks: string): Observable<any> {
    return this.feUtil.httpSvc.patch(API.ASSET.ALARM.UPDATE_ALARM(Number(id)), { alarmDetails: payload, remarks: remarks || null })
  }

  public deleteAlarm(payload: any): Observable<any> {
    return this.feUtil.httpSvc.delete(API.ASSET.ALARM.DELETE(payload.alarm_id), { remarks: payload.remarks })
  }

  public getAssetByLoc(payload: any): Observable<any> {
    return this.feUtil.httpSvc.get(API.ASSET.ASSET_BY_LOC, payload)
  }

  public downloadExcel(payload: any): Observable<any> {
    return this.feUtil.httpSvc.post(API.ASSET.DOWNLOAD_READINGS, payload)
  }
  
  public fetchGraphData(payload: { idt_asset: number | string, date1?: string, date2?: string, timezone?: string }, filter?: { filterType?: string, type: 'RADIO' | 'DATE-RANGE', date?: string[] }): Observable<any> {
    if (filter?.type === 'DATE-RANGE') {
      payload.date1 = filter?.date?.[0];
      payload.date2 = filter?.date?.[1];
    }
    if (filter?.filterType) {
      const { date1, date2 } = this.getDatesByFilterType(filter?.filterType);
      payload.date1 = date1;
      payload.date2 = date2;
    }
    return this.feUtil.httpSvc.get(API.ASSET.GRAPH_BY_DATE, payload)
  }

  public getDatesByFilterType(filterType: string): { date1: string, date2: string } {
    let date1: string = '';
    let date2: string = '';
    const now = DateTime.now().setZone(this.feUtil.time_zone);
    switch (filterType) {
      case 'today':
        date1 = now.startOf('day').toUTC().toISO() || '';
        date2 = now.endOf('day').toUTC().toISO() || '';
        break;
      case 'yesterday':
        const yesterday = now.minus({ days: 1 }).setZone(this.feUtil.time_zone);
        date1 = yesterday.startOf('day').toUTC().toISO() || '';
        date2 = yesterday.endOf('day').toUTC().toISO() || '';
        break;
      case 'week':
        date1 = now.minus({ days: 7 }).startOf('day').toUTC().toISO() || '';
        date2 = now.endOf('day').toUTC().toISO() || '';
        break;
      case 'month':
        date1 = now.minus({ days: 30 }).startOf('day').toUTC().toISO() || '';
        date2 = now.endOf('day').toUTC().toISO() || '';
        break;
    }
    return { date1, date2 };
  }

  public getMatrixData(data: any): { matrix: string, readingData: any[] }[] {
    const result: { [key: string]: any[] } = {};
    if (!data) return [];
    for (const sensorId in data) {
      const sensor = data[sensorId];
      if (!sensor) continue;
      const { datesArray, ...dataArrays } = sensor;
      if (!datesArray) continue;
      Object.entries(dataArrays).forEach(([key, dataArray]: [string, any]) => {
        if (Array.isArray(dataArray) && Array.isArray(datesArray)) {
          const readingData = dataArray.map((value: any, index: number) => {
            const datetime = datesArray[index];
            return { value: value != null ? value.toFixed(2) : null, datetime, sensorId, sensor_type: sensor?.sensor_type };
          })?.filter((reading: any) => reading.value !== null);
          const matrixObj: { sensorId: string, metaData: { matrixType: string, sensorType: string, abbreviation?: string }, timeseriesData: any[] } = {
            sensorId,
            metaData: {
              matrixType: key.replace('Array', ''),
              sensorType: sensor?.sensor_type,
            },
            timeseriesData: readingData,
          };
          if(sensor?.abbreviation) matrixObj.metaData['abbreviation'] = sensor?.abbreviation;
          const matrixType = key.replace('Array', '');
          result[matrixType] = (result[matrixType] || []).concat(matrixObj);
        }
      });
    }
    return Object.entries(result).map(([matrix, readingData]) => ({ matrix, readingData }));
  }

}
